import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { AuthCtx } from "./useAuth";
import { Sentry } from "../lib/sentry";

/* Offline Pro support: cache the last-known profile (incl. pro_until) per user so
   isPro() and the Pro tabs survive with no connectivity. Namespaced by user id so
   a different account on the same device never reads the wrong entitlement. */
const profileKey = (uid) => `byb.profile.${uid}`;
function cacheProfile(p) {
  try {
    if (p?.id) localStorage.setItem(profileKey(p.id), JSON.stringify(p));
  } catch { /* storage full / disabled — non-fatal */ }
}
function readCachedProfile(uid) {
  try {
    const raw = uid && localStorage.getItem(profileKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function clearCachedProfile(uid) {
  try {
    if (uid) localStorage.removeItem(profileKey(uid));
  } catch { /* non-fatal */ }
}

export function AuthProvider({ children }) {
  // session: undefined = still resolving, null = signed out, object = signed in
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Reset profile state the instant the signed-in user changes (including
  // sign-out). Adjusting state during render is React's recommended alternative
  // to a "reset on dependency change" effect, and it keeps the async fetch below
  // free of any synchronous setState in its effect body.
  const userId = session?.user?.id ?? null;
  // Tag Sentry errors with the signed-in user id (id only — no PII).
  useEffect(() => {
    Sentry.setUser(userId ? { id: userId } : null);
  }, [userId]);
  const [profileUserId, setProfileUserId] = useState(null);
  if (userId !== profileUserId) {
    setProfileUserId(userId);
    // Seed from the cached profile so entitlement is correct on the very first
    // render — even offline. The network fetch below refreshes it when online.
    setProfile(readCachedProfile(userId));
    setProfileLoading(!!userId);
  }

  // Fetch the profile for the current user. Keyed on the user id (a primitive)
  // so a token refresh that mints a new session object for the same user
  // doesn't trigger a redundant refetch.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    supabase
      .from("profiles")
      .select("id, username, created_at, onboarded, is_admin, default_settings, pro_until, age_confirmed_at")
      .eq("id", userId)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        if (data) {
          setProfile(data);
          cacheProfile(data);
        } else {
          // offline / fetch failed → keep the last-known cached profile
          setProfile(readCachedProfile(userId));
        }
        setProfileLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function refreshProfile() {
    if (!session) return;
    const { data } = await supabase
      .from("profiles")
      .select("id, username, created_at, onboarded, is_admin, default_settings, pro_until, age_confirmed_at")
      .eq("id", session.user.id)
      .single();
    if (data) {
      setProfile(data);
      cacheProfile(data);
    } else {
      setProfile(readCachedProfile(session.user.id));
    }
  }

  const signOut = () => {
    clearCachedProfile(userId);
    return supabase.auth.signOut();
  };

  return (
    <AuthCtx.Provider value={{ session, profile, profileLoading, refreshProfile, signOut }}>
      {children}
    </AuthCtx.Provider>
  );
}
